import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ProgressIndicator from '../../components/ui/ProgressIndicator';
import AssessmentNavigation from '../../components/ui/AssessmentNavigation';
import SupplierTable from './components/SupplierTable';
import SupplierCard from './components/SupplierCard';
import FilterControls from './components/FilterControls';
import SupplierForm from './components/SupplierForm';
import SupplySummaryPanel from './components/SupplySummaryPanel';
import SupplyTrendChart from './components/SupplyTrendChart';
import BulkImportModal from './components/BulkImportModal';
import Button from '../../components/ui/Button';


const SupplierManagement = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('table');
  const [showForm, setShowForm] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    region: 'all',
    volume: 'all',
    reliability: 'all',
  });

  const mockSuppliers = [
    {
      id: 1,
      name: "Bapak Suharto",
      farmerId: "PTK-001",
      location: "Desa Cibodas, Lembang",
      region: "jawa-barat",
      phone: "081234567890",
      email: "suharto@email.com",
      farmSize: "2.5",
      cattleCount: "15",
      dailyVolume: 120,
      qualityRating: 95,
      qualityStatus: "excellent",
      paymentStatus: "excellent",
      paymentLabel: "Lancar",
      paymentPreference: "weekly",
      bankAccount: "1234567890",
      consistencyScore: 92,
      consistencyStatus: "excellent",
      supplyFluctuation: "8",
      rejectionRate: "2",
      contractDuration: "24",
      notes: "Peternak terpercaya dengan kualitas susu konsisten tinggi"
    },
    {
      id: 2,
      name: "Ibu Siti Aminah",
      farmerId: "PTK-002",
      location: "Desa Ciwidey, Bandung",
      region: "jawa-barat",
      phone: "081234567891",
      email: "siti@email.com",
      farmSize: "1.8",
      cattleCount: "10",
      dailyVolume: 85,
      qualityRating: 88,
      qualityStatus: "good",
      paymentStatus: "good",
      paymentLabel: "Baik",
      paymentPreference: "biweekly",
      bankAccount: "1234567891",
      consistencyScore: 85,
      consistencyStatus: "good",
      supplyFluctuation: "12",
      rejectionRate: "5",
      contractDuration: "18",
      notes: "Peternak dengan potensi pengembangan kapasitas"
    },
    {
      id: 3,
      name: "Bapak Ahmad Yani",
      farmerId: "PTK-003",
      location: "Desa Pangalengan, Bandung",
      region: "jawa-barat",
      phone: "081234567892",
      email: "ahmad@email.com",
      farmSize: "3.2",
      cattleCount: "20",
      dailyVolume: 150,
      qualityRating: 92,
      qualityStatus: "excellent",
      paymentStatus: "excellent",
      paymentLabel: "Lancar",
      paymentPreference: "weekly",
      bankAccount: "1234567892",
      consistencyScore: 88,
      consistencyStatus: "good",
      supplyFluctuation: "10",
      rejectionRate: "3",
      contractDuration: "36",
      notes: "Peternak senior dengan pengalaman lebih dari 15 tahun"
    },
    {
      id: 4,
      name: "Ibu Dewi Lestari",
      farmerId: "PTK-004",
      location: "Desa Rancabali, Ciwidey",
      region: "jawa-barat",
      phone: "081234567893",
      email: "dewi@email.com",
      farmSize: "1.5",
      cattleCount: "8",
      dailyVolume: 65,
      qualityRating: 78,
      qualityStatus: "warning",
      paymentStatus: "warning",
      paymentLabel: "Terlambat",
      paymentPreference: "monthly",
      bankAccount: "1234567893",
      consistencyScore: 72,
      consistencyStatus: "warning",
      supplyFluctuation: "18",
      rejectionRate: "8",
      contractDuration: "12",
      notes: "Memerlukan pendampingan untuk peningkatan kualitas"
    },
    {
      id: 5,
      name: "Bapak Bambang Sutrisno",
      farmerId: "PTK-005",
      location: "Desa Cikole, Lembang",
      region: "jawa-barat",
      phone: "081234567894",
      email: "bambang@email.com",
      farmSize: "2.0",
      cattleCount: "12",
      dailyVolume: 95,
      qualityRating: 90,
      qualityStatus: "excellent",
      paymentStatus: "good",
      paymentLabel: "Baik",
      paymentPreference: "biweekly",
      bankAccount: "1234567894",
      consistencyScore: 86,
      consistencyStatus: "good",
      supplyFluctuation: "11",
      rejectionRate: "4",
      contractDuration: "24",
      notes: "Peternak dengan manajemen peternakan yang baik"
    },
    {
      id: 6,
      name: "Ibu Ratna Sari",
      farmerId: "PTK-006",
      location: "Desa Cihideung, Parongpong",
      region: "jawa-barat",
      phone: "081234567895",
      email: "ratna@email.com",
      farmSize: "1.2",
      cattleCount: "6",
      dailyVolume: 48,
      qualityRating: 65,
      qualityStatus: "critical",
      paymentStatus: "critical",
      paymentLabel: "Bermasalah",
      paymentPreference: "monthly",
      bankAccount: "1234567895",
      consistencyScore: 58,
      consistencyStatus: "critical",
      supplyFluctuation: "25",
      rejectionRate: "15",
      contractDuration: "6",
      notes: "Status kritis - memerlukan evaluasi menyeluruh"
    }
  ];

  const mockTrendData = [
    { date: '01/01', volume: 580, quality: 88 },
    { date: '03/01', volume: 595, quality: 89 },
    { date: '05/01', volume: 610, quality: 90 },
    { date: '07/01', volume: 590, quality: 88 },
    { date: '09/01', volume: 605, quality: 91 },
    { date: '11/01', volume: 620, quality: 92 },
    { date: '13/01', volume: 615, quality: 91 },
    { date: '15/01', volume: 630, quality: 93 },
    { date: '17/01', volume: 625, quality: 92 },
    { date: '19/01', volume: 640, quality: 94 },
    { date: '21/01', volume: 635, quality: 93 },
    { date: '23/01', volume: 650, quality: 95 },
    { date: '25/01', volume: 645, quality: 94 },
    { date: '27/01', volume: 660, quality: 95 },
    { date: '29/01', volume: 655, quality: 94 },
    { date: '31/01', volume: 663, quality: 95 },
  ];

  const filteredSuppliers = useMemo(() => {
    return mockSuppliers?.filter(supplier => {
      const matchesSearch = supplier?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
                           supplier?.farmerId?.toLowerCase()?.includes(filters?.search?.toLowerCase());
      const matchesRegion = filters?.region === 'all' || supplier?.region === filters?.region;
      const matchesVolume = filters?.volume === 'all' ||
                           (filters?.volume === 'high' && supplier?.dailyVolume > 100) ||
                           (filters?.volume === 'medium' && supplier?.dailyVolume >= 50 && supplier?.dailyVolume <= 100) ||
                           (filters?.volume === 'low' && supplier?.dailyVolume < 50);
      const matchesReliability = filters?.reliability === 'all' || supplier?.consistencyStatus === filters?.reliability;

      return matchesSearch && matchesRegion && matchesVolume && matchesReliability;
    });
  }, [filters, mockSuppliers]);

  const summaryData = useMemo(() => {
    const totalVolume = mockSuppliers?.reduce((sum, s) => sum + s?.dailyVolume, 0);
    const avgQuality = Math.round(mockSuppliers?.reduce((sum, s) => sum + s?.qualityRating, 0) / mockSuppliers?.length);
    const avgConsistency = Math.round(mockSuppliers?.reduce((sum, s) => sum + s?.consistencyScore, 0) / mockSuppliers?.length);
    const criticalCount = mockSuppliers?.filter(s => s?.consistencyStatus === 'critical')?.length;
    const warningCount = mockSuppliers?.filter(s => s?.consistencyStatus === 'warning')?.length;

    const regionalDist = [
      { name: 'Jawa Barat', count: mockSuppliers?.filter(s => s?.region === 'jawa-barat')?.length, percentage: 100 },
      { name: 'Jawa Tengah', count: 0, percentage: 0 },
      { name: 'Jawa Timur', count: 0, percentage: 0 },
      { name: 'Banten', count: 0, percentage: 0 },
    ];

    return {
      totalSuppliers: mockSuppliers?.length,
      totalDailyVolume: totalVolume,
      averageQuality: avgQuality,
      supplyConsistency: avgConsistency,
      criticalSuppliers: criticalCount,
      warningSuppliers: warningCount,
      topSupplierPercentage: 50,
      regionalDistribution: regionalDist,
    };
  }, [mockSuppliers]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      region: 'all',
      volume: 'all',
      reliability: 'all',
    });
  };

  const handleAddSupplier = () => {
    setSelectedSupplier(null);
    setShowForm(true);
  };

  const handleEditSupplier = (supplier) => {
    setSelectedSupplier(supplier);
    setShowForm(true);
  };

  const handleDeleteSupplier = (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus data peternak ini?')) {
      console.log('Delete supplier:', id);
    }
  };

  const handleViewDetails = (supplier) => {
    setSelectedSupplier(supplier);
    setShowForm(true);
  };

  const handleSubmitForm = (formData) => {
    console.log('Submit form:', formData);
    setShowForm(false);
    setSelectedSupplier(null);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setSelectedSupplier(null);
  };

  const handleBulkImport = (file) => {
    console.log('Import file:', file);
  };

  const handleNext = () => {
    navigate('/operational-assessment');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ProgressIndicator currentStep={2} completedSteps={[1]} />
      <AssessmentNavigation completedModules={[]} />
      <main className="with-assessment-nav">
        <div className="container-executive py-8 md:py-12">
          {!showForm ? (
            <>
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-8">
                <div>
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                    Manajemen Pemasok
                  </h1>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Kelola data peternak dan analisis stabilitas pasokan susu
                  </p>
                </div>
                <div className="flex items-center gap-3 w-full lg:w-auto">
                  <Button
                    variant="outline"
                    onClick={() => setShowBulkImport(true)}
                    iconName="Upload"
                    iconPosition="left"
                    className="flex-1 lg:flex-none"
                  >
                    Import Data
                  </Button>
                  <Button
                    variant="default"
                    onClick={handleAddSupplier}
                    iconName="Plus"
                    iconPosition="left"
                    className="flex-1 lg:flex-none"
                  >
                    Tambah Peternak
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                <div className="lg:col-span-2 space-y-6">
                  <FilterControls
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onReset={handleResetFilters}
                  />

                  <div className="bg-card rounded-xl border border-border p-4 md:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg md:text-xl font-semibold text-foreground">
                        Daftar Peternak ({filteredSuppliers?.length})
                      </h2>
                      <div className="flex items-center gap-2">
                        <Button
                          variant={viewMode === 'table' ? 'default' : 'ghost'}
                          size="icon"
                          onClick={() => setViewMode('table')}
                          iconName="Table"
                        >
                          <span className="sr-only">Tampilan Tabel</span>
                        </Button>
                        <Button
                          variant={viewMode === 'card' ? 'default' : 'ghost'}
                          size="icon"
                          onClick={() => setViewMode('card')}
                          iconName="LayoutGrid"
                        >
                          <span className="sr-only">Tampilan Kartu</span>
                        </Button>
                      </div>
                    </div>

                    {viewMode === 'table' ? (
                      <div className="hidden lg:block">
                        <SupplierTable
                          suppliers={filteredSuppliers}
                          onEdit={handleEditSupplier}
                          onDelete={handleDeleteSupplier}
                          onViewDetails={handleViewDetails}
                        />
                      </div>
                    ) : null}

                    {viewMode === 'card' || viewMode === 'table' ? (
                      <div className={viewMode === 'table' ? 'lg:hidden' : ''}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {filteredSuppliers?.map(supplier => (
                            <SupplierCard
                              key={supplier?.id}
                              supplier={supplier}
                              onEdit={handleEditSupplier}
                              onDelete={handleDeleteSupplier}
                              onViewDetails={handleViewDetails}
                            />
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <SupplyTrendChart data={mockTrendData} />
                </div>

                <div className="lg:col-span-1">
                  <SupplySummaryPanel summary={summaryData} />
                </div>
              </div>

              <div className="flex items-center justify-between pt-6 border-t border-border">
                <Button
                  variant="outline"
                  onClick={() => navigate('/company-profile-form')}
                  iconName="ArrowLeft"
                  iconPosition="left"
                >
                  Kembali
                </Button>
                <Button
                  variant="default"
                  onClick={handleNext}
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  Lanjut ke Penilaian Operasional
                </Button>
              </div>
            </>
          ) : (
            <div className="max-w-4xl mx-auto">
              <div className="bg-card rounded-xl border border-border p-6 md:p-8">
                <SupplierForm
                  supplier={selectedSupplier}
                  onSubmit={handleSubmitForm}
                  onCancel={handleCancelForm}
                />
              </div>
            </div>
          )}
        </div>
      </main>
      <BulkImportModal
        isOpen={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        onImport={handleBulkImport}
      />
    </div>
  );
};

export default SupplierManagement;